import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { ExternalLink } from "lucide-react";
import Modal from "@/components/controls/Modal";
import { Button } from "@/components/ui/button";
import ClickableLink from "@/components/ui/clickable-link";
import Input from "@/components/ui/input";
import Text from "@/components/ui/text";
import ToastBody from "@/components/ui/toast-body";
import {
  ApiKeyIntegration,
  INTEGRATIONS_TRANSLATION_PREFIX,
  SETUP_TOAST_DURATION
} from "@/pages/settings/components/integrations/integrations";
import { ESIGN_CONNECTION_QUERY } from "@/pages/settings/components/integrations/pandadoc/esignConnection";
import {
  ApiKeyConnectResult,
  useConnectWithApiKey
} from "@/pages/settings/components/integrations/useConnectWithApiKey";

const KEY_INPUT_ID = "integration-api-key-input";

// Sentry Replay and Pendo each drop an element carrying their own class from recordings
export const RECORDING_EXCLUDED_CLASSES = "sentry-block pendo-ignore";

type FailedResult = Exclude<ApiKeyConnectResult, "connected">;

interface ApiKeyConnectModalProps {
  integration: ApiKeyIntegration;
  name: string;
  isOpen: boolean;
  onClose: () => void;
  // A working connection's key is swapped for another; the same connect stores it.
  replacing?: boolean;
}

export const ApiKeyConnectModal: React.FC<ApiKeyConnectModalProps> = ({
  integration,
  name,
  isOpen,
  onClose,
  replacing = false
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const connect = useConnectWithApiKey(integration.connectTo);
  const [apiKey, setApiKey] = useState("");
  const [isConnecting, setIsConnecting] = useState(false);
  const [failure, setFailure] = useState<FailedResult | null>(null);
  const prefix = `${INTEGRATIONS_TRANSLATION_PREFIX}.apiKey`;
  const textPrefix = replacing ? `${prefix}.replace` : prefix;

  const handleClose = () => {
    setApiKey("");
    setFailure(null);
    onClose();
  };

  const handleConnect = async () => {
    const trimmedKey = apiKey.trim();
    if (!trimmedKey || isConnecting) return;
    setIsConnecting(true);
    setFailure(null);
    try {
      const result = await connect(trimmedKey);
      setApiKey("");
      if (result === "connected") {
        // The e-sign status is read separately, so it would still say the key
        // needs replacing.
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["settings/tokens/all"] }),
          queryClient.invalidateQueries({ queryKey: [ESIGN_CONNECTION_QUERY] })
        ]);
        toast.success(
          <ToastBody
            title={t(
              `${INTEGRATIONS_TRANSLATION_PREFIX}.setup.${replacing ? "updated" : "connected"}`,
              { name }
            )}
          />,
          { duration: SETUP_TOAST_DURATION }
        );
        handleClose();
        return;
      }
      setFailure(result);
    } finally {
      // Stays locked through the refresh, so a second key can't be typed and then cleared by this close
      setIsConnecting(false);
    }
  };

  return (
    <Modal
      open={isOpen}
      setIsOpen={(open) => {
        if (!open && !isConnecting) {
          handleClose();
        }
      }}
      contentClassname="w-[35rem]"
      data-test="api-key-connect-modal"
      title={
        <Text as="div" variant="lgSubtitleH1">
          {t(`${textPrefix}.title`, { name })}
        </Text>
      }
      content={
        <form
          className="flex flex-col gap-4 px-6 pt-4 pb-4"
          onSubmit={(event) => {
            event.preventDefault();
            void handleConnect();
          }}
          autoComplete="off"
        >
          <Text variant="bodySecondary">
            {t(`${textPrefix}.intro`, { name })}
          </Text>
          <div className="flex flex-col gap-1">
            <Text variant="bodySecondary">
              {t(`${prefix}.whereToFind`, { name })}
            </Text>
            <ClickableLink
              href={integration.keyPageUrl}
              target="_blank"
              rel="noopener noreferrer"
              stopPropagation={false}
              className="flex w-fit items-center gap-1 text-link transition-colors duration-200 ease-in-out hover:text-link-hover"
            >
              <Text variant="body" className="text-inherit">
                {t(`${prefix}.openKeyPage`, { name })}
              </Text>
              <ExternalLink className="size-4" aria-hidden />
            </ClickableLink>
          </div>
          <div className="flex flex-col gap-1">
            <Text as="label" variant="bodySecondary" htmlFor={KEY_INPUT_ID}>
              {t(`${prefix}.label`)}
            </Text>
            <Input
              id={KEY_INPUT_ID}
              type="password"
              value={apiKey}
              onChange={(event) => {
                setApiKey(event.target.value);
                if (failure) setFailure(null);
              }}
              placeholder={t(`${prefix}.placeholder`)}
              hasError={failure === "rejected"}
              disabled={isConnecting}
              autoComplete="off"
              spellCheck={false}
              autoCorrect="off"
              autoCapitalize="off"
              data-1p-ignore
              data-lpignore="true"
              className={RECORDING_EXCLUDED_CLASSES}
              data-test="api-key-input"
            />
            {failure && (
              <Text
                variant="bodySecondary"
                className="text-error"
                data-test="api-key-error"
              >
                {t(`${prefix}.errors.${failure}`, { name })}
              </Text>
            )}
          </div>
        </form>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isConnecting}
            data-test="api-key-cancel"
          >
            {t("cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleConnect()}
            disabled={!apiKey.trim() || isConnecting}
            data-test="api-key-connect"
          >
            {replacing
              ? t(`${prefix}.replace.${isConnecting ? "submitting" : "submit"}`)
              : isConnecting
                ? t(`${prefix}.connecting`)
                : t(`${prefix}.connect`)}
          </Button>
        </div>
      }
    />
  );
};
